import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Search, ChevronLeft, ChevronRight, Zap, Edit2, Trash2, X } from "lucide-react";
import { API } from "../App";
import { CATEGORY_DISPLAY_NAMES, CATEGORY_GROUPS, TAX_DEDUCTIBLE_CATEGORIES } from "../utils/categoryUtils";

const PAGE_SIZE = 50;

function fmt(n) {
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(n || 0);
}

function CategoryBadge({ category }) {
  const name = CATEGORY_DISPLAY_NAMES[category] || (category || "").replace(/_/g, " ");
  const isDeductible = TAX_DEDUCTIBLE_CATEGORIES.includes(category);
  const isUncategorized = !category || category === "uncategorized";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 600,
      background: isUncategorized ? "#FEF3C7" : isDeductible ? "#EFF6FF" : "#F9FAFB",
      color: isUncategorized ? "#92400E" : isDeductible ? "#2563EB" : "#6B7280",
      border: `1px solid ${isUncategorized ? "#FDE68A" : isDeductible ? "#BFDBFE" : "#E5E7EB"}`,
    }}>
      {isDeductible && "✓ "}{name || "Needs review"}
    </span>
  );
}

export default function Transactions() {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal]               = useState(0);
  const [page, setPage]                 = useState(1);
  const [totalPages, setTotalPages]     = useState(1);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState("");
  const [filterCat, setFilterCat]       = useState("");
  const [filterType, setFilterType]     = useState("");
  const [editTx, setEditTx]             = useState(null);
  const [editCat, setEditCat]           = useState("");
  const [saving, setSaving]             = useState(false);
  const [totalUncategorized, setTotalUncategorized] = useState(0);

  const token   = localStorage.getItem("taxbuddy_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchUncategorizedCount = useCallback(async () => {
    try {
      const tok = localStorage.getItem("taxbuddy_token");
      const res = await axios.get(`${API}/api/transactions?category=uncategorized&limit=1`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setTotalUncategorized(res.data.total || 0);
    } catch {}
  }, []);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
        sort: "date",
        order: "desc",
      });
      if (search)     params.append("search", search);
      if (filterCat)  params.append("category", filterCat);
      if (filterType) params.append("type", filterType);

      const res = await axios.get(`${API}/api/transactions?${params}`, { headers });
      const data = res.data;

      // Handle both { transactions: [...], total, pages } and plain array
      if (Array.isArray(data)) {
        setTransactions(data);
        setTotal(data.length);
        setTotalPages(1);
      } else {
        setTransactions(data.transactions || data.data || []);
        setTotal(data.total || 0);
        setTotalPages(data.pages || 1);
      }
    } catch (e) {
      toast.error("Could not load transactions");
    } finally {
      setLoading(false);
    }
  }, [page, search, filterCat, filterType]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);
  useEffect(() => { fetchUncategorizedCount(); }, [fetchUncategorizedCount]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [search, filterCat, filterType]);

  const handleSaveCategory = async (txId) => {
    if (!editCat) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/api/transactions/${txId}`, { category: editCat }, { headers });
      toast.success("Category saved");
      setEditTx(null);
      fetchTransactions();
      fetchUncategorizedCount();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (txId) => {
    if (!window.confirm("Delete this transaction?")) return;
    try {
      await axios.delete(`${API}/api/transactions/${txId}`, { headers });
      toast.success("Deleted");
      fetchTransactions();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: 0 }}>Transactions</h1>
          <p style={{ fontSize: 13, color: "#6B7280", marginTop: 4 }}>
            {total} total · {totalUncategorized > 0 ? `${totalUncategorized} need review` : "all categorised"}
          </p>
        </div>
        {totalUncategorized > 0 && (
          <button
            onClick={() => navigate("/quick-sort")}
            style={{ display: "flex", alignItems: "center", gap: 8, background: "#F59E0B", color: "#fff", border: "none", borderRadius: 10, padding: "10px 18px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
          >
            <Zap size={15} /> Quick Sort ({totalUncategorized})
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 180 }}>
          <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9CA3AF" }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search transactions…"
            style={{ width: "100%", paddingLeft: 32, paddingRight: 10, paddingTop: 8, paddingBottom: 8, border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, outline: "none", boxSizing: "border-box" }}
          />
          {search && (
            <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF" }}>
              <X size={13} />
            </button>
          )}
        </div>

        <select
          value={filterCat}
          onChange={e => setFilterCat(e.target.value)}
          style={{ padding: "8px 12px", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, outline: "none", background: "#fff" }}
        >
          <option value="">All categories</option>
          {Object.entries(CATEGORY_GROUPS).map(([group, cats]) => (
            <optgroup key={group} label={group}>
              {cats.map(cat => (
                <option key={cat} value={cat}>
                  {TAX_DEDUCTIBLE_CATEGORIES.includes(cat) ? "✓ " : ""}{CATEGORY_DISPLAY_NAMES[cat] || cat}
                </option>
              ))}
            </optgroup>
          ))}
        </select>

        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          style={{ padding: "8px 12px", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, outline: "none", background: "#fff" }}
        >
          <option value="">All types</option>
          <option value="credit">Income only</option>
          <option value="debit">Expenses only</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, overflow: "hidden" }}>
        {/* Column headers */}
        <div style={{ display: "grid", gridTemplateColumns: "110px 1fr 1fr 160px 110px 80px", gap: 0, padding: "10px 16px", background: "#F9FAFB", borderBottom: "1px solid #E5E7EB", fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          <div>Date</div>
          <div>Description</div>
          <div>Merchant</div>
          <div>Category</div>
          <div style={{ textAlign: "right" }}>Amount</div>
          <div style={{ textAlign: "center" }}>Edit</div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>Loading…</div>
        ) : transactions.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>
            No transactions found.
            {!filterCat && !filterType && !search && (
              <div style={{ marginTop: 8 }}>Upload a bank statement to get started.</div>
            )}
          </div>
        ) : (
          transactions.map(tx => {
            const txId = tx.id || tx._id;
            const isCredit = tx.type === "credit";
            const isEditing = editTx === txId;
            return (
              <div key={txId} style={{ display: "grid", gridTemplateColumns: "110px 1fr 1fr 160px 110px 80px", gap: 0, padding: "12px 16px", borderBottom: "1px solid #F3F4F6", alignItems: "center", background: isEditing ? "#F0FDF4" : "#fff" }}>
                <div style={{ fontSize: 12, color: "#6B7280" }}>{tx.date?.slice(0, 10)}</div>
                <div style={{ fontSize: 13, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 8 }}>{tx.description}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 8 }}>{tx.merchant || tx.description}</div>
                <div>
                  {isEditing ? (
                    <div style={{ display: "flex", gap: 6 }}>
                      <select
                        value={editCat}
                        onChange={e => setEditCat(e.target.value)}
                        style={{ fontSize: 12, padding: "4px 6px", border: "1px solid #D1D5DB", borderRadius: 6, outline: "none", flex: 1 }}
                      >
                        <option value="">Select…</option>
                        {Object.entries(CATEGORY_GROUPS).map(([group, cats]) => (
                          <optgroup key={group} label={group}>
                            {cats.map(cat => (
                              <option key={cat} value={cat}>
                                {TAX_DEDUCTIBLE_CATEGORIES.includes(cat) ? "✓ " : ""}{CATEGORY_DISPLAY_NAMES[cat] || cat}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      <button
                        onClick={() => handleSaveCategory(txId)}
                        disabled={saving || !editCat}
                        style={{ fontSize: 11, padding: "4px 8px", background: "#1A4D2E", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, opacity: saving || !editCat ? 0.5 : 1 }}
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <CategoryBadge category={tx.category} />
                  )}
                </div>
                <div style={{ textAlign: "right", fontSize: 13, fontWeight: 700, color: isCredit ? "#059669" : "#111827" }}>
                  {isCredit ? "+" : "-"}{fmt(tx.amount)}
                </div>
                <div style={{ display: "flex", justifyContent: "center", gap: 4 }}>
                  <button
                    onClick={() => { setEditTx(isEditing ? null : txId); setEditCat(tx.category || ""); }}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4, borderRadius: 4 }}
                    title="Edit category"
                  >
                    {isEditing ? <X size={13} /> : <Edit2 size={13} />}
                  </button>
                  <button
                    onClick={() => handleDelete(txId)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4, borderRadius: 4 }}
                    title="Delete"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 16 }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 12px", cursor: page === 1 ? "not-allowed" : "pointer", opacity: page === 1 ? 0.4 : 1 }}
          >
            <ChevronLeft size={14} />
          </button>
          <span style={{ fontSize: 13, color: "#6B7280" }}>Page {page} of {totalPages} · {total} transactions</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 12px", cursor: page === totalPages ? "not-allowed" : "pointer", opacity: page === totalPages ? 0.4 : 1 }}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
